import { useState } from "react";
import { useNavigate } from "react-router-dom";
import UserAddEdit from "./UserAddEdit";
import Button from "../../ui/Button";
import { AddIcon, DeleteIcon, EditIcon, ViewIcon } from "../../utils/icon.utils";
import Table from "../../ui/Table/Table";
import { GroupBtn } from "../../ui/GroupBtn";
import { useDeleteUserMutation, useGetAllUserQuery } from "../../utils/user.utils";
import { tableIndex } from "../../utils/table.utils";
import { onConfirm } from "../../utils/sweetAlert.utils";
import Loading from "../../components/Loading";
import PageLayout from "../../layouts/PageLayout";
import { useAppPermissions } from "../../utils/app.utils";
import { arrayMap } from "../../utils/array.utils";

export default function UserPage() {
  const pageSize = 10;
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [editItem, setEditItem] = useState(null);
  const appPermission = useAppPermissions();

  const userPermission = appPermission?.user;

  const [modal, setModal] = useState(false);

  const toggle = () => {
    if (modal) {
      setEditItem(null);
    }
    setModal((prev) => !prev);
  };

  const { loading, data } = useGetAllUserQuery({
    query: `?page=${currentPage}&size=${pageSize}`
  });

  const { loading: deleting, deleteUser } = useDeleteUserMutation(["deleteUser"]);

  const totalPage = data?.last_page === 1 && data?.total === 0 ? 0 : data?.last_page;

  const breadCrumbs = [
    {
      name: "Settings",
      link: "/settings"
    },
    {
      name: "User"
    }
  ];

  const thead = ["#", "Name", "Username", "Email", "Role", "Action"];

  const tbody =
    arrayMap(data?.data, (item, index) => ({
      "#": tableIndex(index, currentPage, pageSize),
      Name: <div>
        {item?.name ?? "-"}
        <br />
        <small className="text-muted">{item?.designation ?? ""}</small>
      </div>,
      Username: item?.username ?? "-",
      Email: item?.email ?? "-",
      Role: item?.role?.name ?? "-",
      ...item
    })) || [];

  const ActionComponent = ({ data }) => {
    const handleEdit = (item) => {
      setEditItem(item);
      toggle();
    };

    const handleView = (item) => {
      navigate(`/user/${item?.username ?? ""}/profile`);
    };

    const handleDelete = async (item) => {
      const { isConfirmed } = await onConfirm();
      if (isConfirmed) {
        await deleteUser(item?.id);
      }
    };

    let btns = [];

    if (userPermission?.delete) {
      btns.push({
        icon: DeleteIcon,
        onClick: () => handleDelete(data),
        color: "danger"
      });
    }

    if (userPermission?.update) {
      btns.push({
        icon: EditIcon,
        onClick: () => handleEdit(data),
        color: "primary"
      });
    }

    if (userPermission?.view) {
      btns.push({
        icon: ViewIcon,
        onClick: () => handleView(data),
        color: "secondary"
      });
    }

    return <GroupBtn btns={btns} justify="start" />;
  };

  const isLoading = loading || deleting;

  return (
    <PageLayout title="User" subTitle={`Total: ${data?.total ?? 0}`} breadCrumbs={breadCrumbs}>
      {userPermission?.create && (
        <div className="text-end">
          <Button color="primary" icon={AddIcon} name="Create User" onClick={toggle} />
        </div>
      )}

      {loading ? "Loading..." : ""}

      <Table
        thead={thead}
        tbody={tbody}
        action={ActionComponent}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPage={totalPage}
      />

      {modal && <UserAddEdit modal={modal} toggle={toggle} editItem={editItem} />}
      {<Loading loading={isLoading} />}
    </PageLayout>
  );
}
